import { Component, OnDestroy, OnInit } from "@angular/core";
import { ActivatedRoute, Router } from "@angular/router";
import { IconService } from "app/bundles/requests/shared/services/icon.service";
import { RequestService } from "app/bundles/requests/shared/services/request.service";
import { ConfirmDialogService } from "app/core/confirm-dialog.service";
import { CoreService } from "app/core/core.service";
import { environment } from "environments/environment";
import { Subscription, of, switchMap, tap } from "rxjs";
import { StorageService } from "../../../shared/services/storage.service";
import {
  CloseWorkorderCollapseItem,
  CollapseItem,
  CollapseItemValidation,
  CollapseItemsEnum,
  ItemPropertiesVisibility,
} from "../shared/models/collapseItem";
import {
  IWorkorderOrServiceRequestSummary,
  IWorkorderOrServiceRequestDetail,
  IWorkorderRequestSummary,
  RequestSourceTypeEnum,
} from "../shared/models/request";
import { EditRequestService } from "../shared/services/edit-request.service";
import { StatusService } from "../shared/services/status.service";

@Component({
  selector: "app-edit-request",
  templateUrl: "./edit-request.component.html",
  styleUrls: ["./edit-request.component.scss"],
})
export class EditRequestComponent implements OnInit, OnDestroy {
  public request: IWorkorderOrServiceRequestDetail;
  public requestSummary: IWorkorderOrServiceRequestSummary;
  private subscriptions = new Subscription();
  public collapseItemsEnum = CollapseItemsEnum;
  public actionFromMenu: CollapseItemsEnum;

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private storageService: StorageService,
    public editRequestService: EditRequestService,
    private statusService: StatusService,
    public iconService: IconService,
    private requestService: RequestService,
    private confirmDialogService: ConfirmDialogService,
    private coreService: CoreService,
  ) {
    if (this.router.getCurrentNavigation().extras.state.actionFromMenu) {
      this.actionFromMenu =
        this.router.getCurrentNavigation().extras.state.actionFromMenu;
    }
  }

  public ngOnInit() {
    this.subscriptions.add(
      this.route.data.subscribe(
        (data: { request: IWorkorderOrServiceRequestDetail }) => {
          this.request = data.request;
          this.requestSummary = this.request.requestSummary;
          this.editRequestService.clearAllCollapseItems();
          const disabledSessions = this.statusService.getDisabledSessions(
            this.request.status.code,
            this.requestSummary?.["type"],
          );

          const returnDDSDisabledMsg =
            this.request.source === RequestSourceTypeEnum.workorderRequest &&
            this.request.type === "I"
              ? "internalWorkorderDisabledMsg"
              : disabledSessions.includes("returnDDS")
              ? "workorderStatusDisabledMsg"
              : "";

          // take in charge service request session
          this.editRequestService.addCollapseItem(
            new CollapseItem(
              this.collapseItemsEnum.takeInCharge,
              new ItemPropertiesVisibility(
                false,
                false,
                false,
                false,
                true,
                false,
              ),
              new CollapseItemValidation(
                false,
                false,
                false,
                false,
                true,
                false,
              ),
              disabledSessions.includes("takeInCharge"),
            ),
          );

          // change activity service request session
          this.editRequestService.addCollapseItem(
            new CollapseItem(
              this.collapseItemsEnum.updateActivity,
              new ItemPropertiesVisibility(
                false,
                false,
                false,
                false,
                false,
                true,
              ),
              new CollapseItemValidation(
                false,
                false,
                false,
                false,
                false,
                true,
              ),
              disabledSessions.includes("updateActivity"),
            ),
          );

          // change responsible service request session
          this.editRequestService.addCollapseItem(
            new CollapseItem(
              this.collapseItemsEnum.updateResponsible,
              new ItemPropertiesVisibility(
                false,
                false,
                false,
                false,
                true,
                false,
              ),
              new CollapseItemValidation(
                false,
                false,
                false,
                false,
                true,
                false,
              ),
              disabledSessions.includes("updateResponsible"),
            ),
          );

          // close workorder session
          this.editRequestService.addCollapseItem(
            new CloseWorkorderCollapseItem(
              this.collapseItemsEnum.closeWorkorder,
              new ItemPropertiesVisibility(
                true,
                true,
                environment.capabilities.photosActive,
                true,
                false,
                false,
              ),
              new CollapseItemValidation(
                false,
                false,
                false,
                false,
                false,
                false,
              ),
              disabledSessions.includes("closeWorkorder"),
            ),
          );

          // change status session
          this.editRequestService.addCollapseItem(
            new CollapseItem(
              this.collapseItemsEnum.changeStatus,
              new ItemPropertiesVisibility(
                true,
                true,
                environment.capabilities.photosActive,
                true,
                false,
                false,
              ),
              new CollapseItemValidation(
                true,
                false,
                false,
                false,
                false,
                false,
              ),
              disabledSessions.includes("changeStatus"),
            ),
          );

          // return dds session
          this.editRequestService.addCollapseItem(
            new CollapseItem(
              this.collapseItemsEnum.returnDDS,
              new ItemPropertiesVisibility(
                false,
                true,
                environment.capabilities.photosActive,
                true,
                false,
                false,
              ),
              new CollapseItemValidation(
                false,
                true,
                false,
                true,
                false,
                false,
              ),
              disabledSessions.includes("returnDDS"),
              returnDDSDisabledMsg,
            ),
          );

          // add photo session
          this.editRequestService.addCollapseItem(
            new CollapseItem(
              this.collapseItemsEnum.addPhoto,
              new ItemPropertiesVisibility(
                false,
                false,
                environment.capabilities.photosActive,
                false,
                false,
                false,
              ),
              new CollapseItemValidation(
                false,
                false,
                true,
                false,
                false,
                false,
              ),
              !environment.capabilities.photosActive ||
                disabledSessions.includes("addPhoto"),
            ),
          );

          // internal comments session
          this.editRequestService.addCollapseItem(
            new CollapseItem(
              this.collapseItemsEnum.addInternalComment,
              new ItemPropertiesVisibility(
                false,
                false,
                false,
                true,
                false,
                false,
              ),
              new CollapseItemValidation(
                false,
                false,
                false,
                true,
                false,
                false,
              ),
              disabledSessions.includes("addInternalComment"),
            ),
          );

          // cancel take in charge
          this.editRequestService.addCollapseItem(
            new CollapseItem(
              this.collapseItemsEnum.cancelTakeInCharge,
              new ItemPropertiesVisibility(
                false,
                false,
                false,
                false,
                false,
                false,
              ),
              new CollapseItemValidation(
                false,
                false,
                false,
                false,
                false,
                false,
              ),
              disabledSessions.includes("cancelTakeInCharge"),
            ),
          );
        },
      ),
    );

    this.editRequestService.requestChangedObservable.subscribe(
      (request) => this.request,
    );

    if (this.actionFromMenu) {
      this.editRequestService.toggleCollapse(this.actionFromMenu);
    }
  }

  public ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  public backToRequestList(): void {
    void this.router.navigate(["list"]);
  }

  public onCancelTakeInCharge() {
    this.confirmDialogService.createConfirmDialog({
      confirmedCB: () => {
        return of(null).pipe(
          tap((x) => {
            this.coreService.startSpinner();
          }),
          switchMap((x) =>
            this.requestService.cancelTakeInCharge([this.request.id]),
          ),
          tap((res) => {
            this.storageService.savePositionList(
              res.success[0].ddsId.toString(),
            );
          }),
          switchMap((x) => {
            return this.requestService.getGeojsonObservable();
          }),
          tap((x) => this.backToRequestList()),
        );
      },
      bodyMessage:
        "components.confirmDialogComponent.cancelTakeInChargeMessage",
    });
  }
}
